import React, { useRef, useEffect } from 'react';
import { NutritionDaySummary, NutritionSummary } from '../../hooks/useNutritionSummary';
import { GoalTarget } from '../client/HealthCalendarTile';
import { getMetricComplianceColor } from '../../utils/goalCompliance';

/**
 * NUTRITION TABLE - Phase 9b + 9c
 *
 * Full nutrition data table with all 12 columns.
 * Mirrors FatSecret food diary style.
 *
 * Features:
 * - Sticky header row
 * - Frozen date column on horizontal scroll
 * - Totals/averages row at bottom
 * - Responsive horizontal scroll
 * - Clickable dates for drill-down navigation (Phase 9c)
 */

export interface WeightData {
  weight: number | null;
  delta: number | null;
}

interface NutritionTableProps {
  summary: NutritionSummary;
  onDateClick?: (date: string) => void;
  onRowSelect?: (date: string | null) => void;
  selectedDate?: string | null;
  weightData?: Map<string, WeightData>;
  goalsData?: Map<string, Record<string, GoalTarget>>;
}

// Column definitions with units and grouping
// divider: 'thick' = thick border after this column, 'thin' = normal border
const columns = [
  { key: 'date', label: 'Date', unit: '', align: 'left' as const, frozen: true, divider: 'thick' as const },
  { key: 'weight', label: 'Weight', unit: '', align: 'right' as const, divider: 'thin' as const },
  { key: 'weight_delta', label: '\u0394', unit: '', align: 'right' as const, divider: 'thick' as const },
  { key: 'calories', label: 'Cal', unit: '', align: 'right' as const, divider: 'thick' as const },
  // Fats group
  { key: 'fats_g', label: 'Fat', unit: 'g', align: 'right' as const, divider: 'thin' as const },
  { key: 'saturated_fat_g', label: 'Sat', unit: 'g', align: 'right' as const, divider: 'thin' as const },
  { key: 'polyunsaturated_fat_g', label: 'Poly', unit: 'g', align: 'right' as const, divider: 'thin' as const },
  { key: 'monounsaturated_fat_g', label: 'Mono', unit: 'g', align: 'right' as const, divider: 'thick' as const },
  // Carbs group
  { key: 'carbs_g', label: 'Carbs', unit: 'g', align: 'right' as const, divider: 'thin' as const },
  { key: 'fiber_g', label: 'Fiber', unit: 'g', align: 'right' as const, divider: 'thin' as const },
  { key: 'sugar_g', label: 'Sugar', unit: 'g', align: 'right' as const, divider: 'thick' as const },
  // Protein
  { key: 'protein_g', label: 'Protein', unit: 'g', align: 'right' as const, divider: 'thick' as const },
  // Minerals group
  { key: 'cholesterol_mg', label: 'Chol', unit: 'mg', align: 'right' as const, divider: 'thin' as const },
  { key: 'sodium_mg', label: 'Sodium', unit: 'mg', align: 'right' as const, divider: 'thin' as const },
  { key: 'potassium_mg', label: 'Potas', unit: 'mg', align: 'right' as const, divider: 'none' as const },
];

// Nutrition table column keys that have matching targets_at_time fields
// Column keys (calories, carbs_g, sodium_mg, etc.) match health_data_field names directly
const goalColumns = new Set(['calories', 'carbs_g', 'protein_g', 'sodium_mg', 'fats_g', 'fiber_g']);

// Build a human-readable tooltip from a GoalTarget
const goalTooltip = (goal: GoalTarget | undefined, col: typeof columns[number]): string | undefined => {
  if (!goal) return undefined;
  const unit = col.unit ? col.unit : '';
  const fmt = (v: number) => v.toLocaleString();
  switch (goal.goal_type) {
    case 'range':
      return `Goal: ${fmt(goal.floor_value || 0)} – ${fmt(goal.goal_value)}${unit ? ' ' + unit : ''}`;
    case 'over':
      return `Goal: Over ${fmt(goal.goal_value)}${unit ? ' ' + unit : ''}`;
    case 'under':
      return `Goal: Under ${fmt(goal.goal_value)}${unit ? ' ' + unit : ''}`;
    case 'target':
      return `Goal: ${fmt(goal.goal_value)}${unit ? ' ' + unit : ''} (±${goal.variance_percent || 5}%)`;
    case 'tracking_only':
      return `Tracking only (no target)`;
    default:
      return undefined;
  }
};

export const NutritionTable: React.FC<NutritionTableProps> = ({ summary, onDateClick, onRowSelect, selectedDate, weightData, goalsData }) => {
  const { days } = summary;
  const tbodyRef = useRef<HTMLTableSectionElement>(null);

  // Format date for display: "Mon 12/25"
  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr + 'T00:00:00');
    const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return `${dayName} ${month}/${day}`;
  };

  // Format number value - always round to whole number
  const formatValue = (value: number | undefined, unit: string): string => {
    if (value === undefined || value === null) return '-';
    if (value === 0) return '-';
    const rounded = Math.round(value);
    return unit ? `${rounded}` : rounded.toLocaleString();
  };

  // Get cell value from day data
  const getCellValue = (day: NutritionDaySummary, key: string): number | string | null => {
    if (key === 'date') return formatDate(day.date);
    if (key === 'weight') return weightData?.get(day.date)?.weight ?? null;
    if (key === 'weight_delta') return weightData?.get(day.date)?.delta ?? null;
    return (day as any)[key] ?? 0;
  };

  // Calculate average for a column
  const getColumnAverage = (key: string): number | null => {
    if (key === 'weight_delta') return null; // No average for delta
    if (key === 'weight') {
      if (!weightData) return null;
      const weights = days
        .map(d => weightData.get(d.date)?.weight)
        .filter((w): w is number => w != null && w > 0);
      return weights.length > 0 ? weights.reduce((a, b) => a + b, 0) / weights.length : null;
    }
    const daysWithData = days.filter(d => d.calories > 0);
    if (daysWithData.length === 0) return 0;
    const total = daysWithData.reduce((sum, day) => sum + ((day as any)[key] ?? 0), 0);
    return total / daysWithData.length;
  };

  // Auto-scroll to selected row
  useEffect(() => {
    if (!selectedDate) return;
    const timer = setTimeout(() => {
      const tbody = tbodyRef.current;
      if (!tbody) return;
      const row = tbody.querySelector('.highlighted-row') as HTMLElement;
      if (!row) return;
      const rowCenter = (row.offsetTop - tbody.offsetTop) + row.offsetHeight / 2;
      const tbodyCenter = tbody.clientHeight / 2;
      tbody.scrollTo({ top: rowCenter - tbodyCenter, behavior: 'smooth' });
    }, 50);
    return () => clearTimeout(timer);
  }, [selectedDate]);

  // Build footer data — mirrors analytics pattern: daily values on selection, month avg otherwise
  const getFooterData = () => {
    const daysWithData = days.filter(d => d.calories > 0);
    const isDay = selectedDate != null;
    const label = isDay ? 'Daily' : 'Month Avg';
    // Selected day: show that day's raw values with compliance coloring
    if (isDay) {
      const day = daysWithData.find(d => d.date === selectedDate);
      if (!day) return { label, values: {} as Record<string, number | null>, colors: {} as Record<string, string | null> };

      const values: Record<string, number | null> = {};
      for (const col of columns.slice(1)) {
        if (col.key === 'weight') {
          values[col.key] = weightData?.get(selectedDate)?.weight ?? null;
        } else if (col.key === 'weight_delta') {
          values[col.key] = weightData?.get(selectedDate)?.delta ?? null;
        } else {
          values[col.key] = (day as any)[col.key] ?? null;
        }
      }

      const targets = goalsData?.get(selectedDate);
      const colors: Record<string, string | null> = {};
      for (const col of columns.slice(1)) {
        if (goalColumns.has(col.key) && targets) {
          colors[col.key] = getMetricComplianceColor(values[col.key] ?? null, targets[col.key]);
        }
      }

      return { label, values, colors };
    }

    // Month average — no compliance coloring (goals may change mid-month)
    const values: Record<string, number | null> = {};
    for (const col of columns.slice(1)) {
      values[col.key] = getColumnAverage(col.key);
    }

    return { label, values, colors: {} as Record<string, string | null> };
  };

  const footerData = getFooterData();

  if (days.length === 0) {
    return (
      <div className="nutrition-table-empty">
        <p>No nutrition data for the selected date range.</p>
      </div>
    );
  }

  return (
    <div className="nutrition-table-wrapper">
        <table className="nutrition-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`${col.frozen ? 'frozen-col' : ''} divider-${col.divider}`}
                >
                  <span className="th-label">{col.label}</span>
                  {col.unit && <span className="th-unit">({col.unit})</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody ref={tbodyRef}>
            {days.map((day) => {
              const hasData = day.calories > 0;
              const isSelected = selectedDate === day.date;

              return (
                <tr
                  key={day.date}
                  className={`${hasData ? '' : 'no-data'} ${isSelected ? 'highlighted-row' : ''}`}
                  onClick={() => { if (hasData && onRowSelect) onRowSelect(selectedDate === day.date ? null : day.date); }}
                  style={{ cursor: hasData ? 'pointer' : 'default' }}
                >
                  {columns.map((col) => {
                    const value = getCellValue(day, col.key);
                    const dayGoals = goalsData?.get(day.date);
                    const tip = goalColumns.has(col.key) ? goalTooltip(dayGoals?.[col.key], col) : undefined;
                    const cellColor = hasData && goalColumns.has(col.key) ? getMetricComplianceColor(value as number, dayGoals?.[col.key]) : null;
                    return (
                      <td
                        key={col.key}
                        title={tip}
                        className={`${col.frozen ? 'frozen-col' : ''} align-${col.align} divider-${col.divider}${col.key === 'weight_delta' && value != null ? (Number(value) < 0 ? ' change-loss' : Number(value) > 0 ? ' change-gain' : '') : ''}`}
                      >
                        {col.key === 'date' ? (
                          hasData && onDateClick ? (
                            <button
                              className="date-link"
                              onClick={(e) => { e.stopPropagation(); onDateClick(day.date); }}
                            >
                              {value}
                            </button>
                          ) : (
                            <span className="date-cell">{value}</span>
                          )
                        ) : col.key === 'weight' ? (
                          <span>{value != null ? Number(value).toFixed(1) : '—'}</span>
                        ) : col.key === 'weight_delta' ? (
                          <span>{value != null ? `${Number(value) > 0 ? '+' : ''}${Number(value).toFixed(1)}` : '—'}</span>
                        ) : (
                          <span className={hasData ? '' : 'empty-value'} style={cellColor ? { color: cellColor } : undefined}>
                            {formatValue(value as number, col.unit)}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="averages-row">
              <td className="frozen-col align-left divider-thick">
                <span className="summary-label">{footerData.label}</span>
              </td>
              {columns.slice(1).map((col) => {
                const val = footerData.values[col.key];
                const color = footerData.colors[col.key];
                const isDelta = col.key === 'weight_delta';
                const isWeight = col.key === 'weight';
                return (
                  <td
                    key={col.key}
                    className={`align-${col.align} divider-${col.divider}${isDelta && val != null ? (Number(val) < 0 ? ' change-loss' : Number(val) > 0 ? ' change-gain' : '') : ''}`}
                  >
                    <span className="avg-value" style={
                      color ? { color } :
                      isDelta && val != null ? { color: Number(val) < 0 ? 'var(--accent-green)' : Number(val) > 0 ? '#ef4444' : undefined } :
                      undefined
                    }>
                      {isWeight ? (val != null ? Number(val).toFixed(1) : '') :
                       isDelta ? (val != null ? `${Number(val) > 0 ? '+' : ''}${Number(val).toFixed(1)}` : '') :
                       formatValue(val as number, col.unit)}
                    </span>
                  </td>
                );
              })}
            </tr>
          </tfoot>
        </table>

      <style>{styles}</style>
    </div>
  );
};

const styles = `
  .nutrition-table-wrapper {
    height: 100%;
    display: flex;
    flex-direction: column;
    background: var(--bg-secondary);
    border: 1px solid var(--border-secondary);
    border-radius: var(--radius-medium);
    overflow: hidden;
  }

  .nutrition-table {
    width: 100%;
    font-size: 13px;
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  /* Header */
  .nutrition-table thead {
    flex-shrink: 0;
    display: block;
    width: 100%;
    overflow-y: auto;
    scrollbar-gutter: stable;
    box-shadow: 0 3px 0 0 var(--text-tertiary, #555);
    z-index: 20;
    background: var(--bg-tertiary);
  }

  .nutrition-table thead tr {
    display: table;
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
  }

  .nutrition-table th {
    background: var(--bg-tertiary);
    border-bottom: none;
    padding: 10px 12px;
    font-weight: 600;
    color: var(--text-secondary);
    white-space: nowrap;
    text-align: center;
  }

  .th-label {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .th-unit {
    font-size: 10px;
    font-weight: 400;
    color: var(--text-tertiary);
    margin-left: 2px;
  }

  /* Date column left-aligned */
  .nutrition-table th.frozen-col {
    text-align: left;
  }

  /* Body */
  .nutrition-table tbody {
    flex: 1;
    display: block;
    overflow-y: auto;
    scrollbar-gutter: stable;
    min-height: 0;
  }

  .nutrition-table tbody tr {
    display: table;
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
  }

  .nutrition-table td {
    padding: 8px 12px;
    border-bottom: 1px solid var(--border-secondary);
    color: var(--text-primary);
  }

  .nutrition-table tbody tr:hover {
    background: var(--bg-tertiary);
  }

  .nutrition-table tbody tr.no-data {
    opacity: 0.5;
  }

  .nutrition-table tbody tr.no-data:hover {
    background: transparent;
  }

  .date-cell {
    font-weight: 500;
    color: var(--text-primary);
  }

  .nutrition-table .highlighted-row td {
    background: var(--row-highlight);
  }

  .nutrition-table .highlighted-row .frozen-col {
    background: var(--row-highlight);
  }

  .date-link {
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    font-weight: 500;
    color: var(--accent-blue);
    cursor: pointer;
    text-decoration: none;
    transition: color 0.15s;
  }

  .date-link:hover {
    color: var(--accent-blue);
    text-decoration: underline;
  }

  .empty-value {
    color: var(--text-tertiary);
  }

  .nutrition-table td.change-loss,
  .nutrition-table .averages-row td.change-loss {
    color: var(--accent-green);
  }

  .nutrition-table td.change-gain,
  .nutrition-table .averages-row td.change-gain {
    color: #ef4444;
  }

  /* Frozen date column */
  .frozen-col {
    position: sticky;
    left: 0;
    background: var(--bg-secondary);
    z-index: 10;
    border-right: 1px solid var(--border-secondary);
  }

  thead .frozen-col {
    background: var(--bg-tertiary);
  }

  tbody tr:hover .frozen-col {
    background: var(--bg-tertiary);
  }

  /* Alignment */
  .align-left { text-align: left; }
  .align-right { text-align: right; }

  /* Column Dividers */
  .divider-thin {
    border-right: 1px solid var(--border-secondary);
  }

  .divider-thick {
    border-right: 2px solid var(--text-tertiary);
  }

  .divider-none {
    border-right: none;
  }

  /* Footer - Totals & Averages */
  .nutrition-table tfoot {
    flex-shrink: 0;
    display: block;
    width: 100%;
    overflow-y: auto;
    scrollbar-gutter: stable;
    box-shadow: 0 -3px 0 0 var(--text-tertiary, #555);
    z-index: 10;
    background: var(--bg-tertiary);
  }

  .nutrition-table tfoot tr {
    display: table;
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
  }

  .nutrition-table tfoot td {
    background: var(--bg-tertiary);
    border-top: none;
    border-bottom: none;
    padding: 10px 12px;
  }

  .summary-label {
    font-weight: 600;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-secondary);
  }

  .avg-value {
    color: var(--text-primary);
    font-weight: 500;
  }

  .nutrition-table tfoot .frozen-col {
    background: var(--bg-tertiary);
  }

  /* Empty state */
  .nutrition-table-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 200px;
    color: var(--text-secondary);
    font-size: 14px;
  }
`;

export default NutritionTable;
